// Phone input — displays "0912 345 678", normalizes to clean VN mobile digits.
// Hỗ trợ bộ gõ Tiếng Việt (Telex/VNI/Unikey/EVKey): chống nuốt chữ, chống giật con trỏ, tự động đổi o/O thành 0.

import * as React from 'react'
import { Phone, CheckCircle2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { normalizePhoneVN, isValidVnPhone, getPhoneValidationError } from '@/lib/format'

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string | null | undefined
  onValueChange: (val: string | null) => void
  error?: string
  showValidation?: boolean
  containerClassName?: string
  /** Ẩn icon điện thoại trang trí bên trái (form gọn đã có nhãn "Điện thoại") */
  hideIcon?: boolean
}

/** Group local number into 0912 345 678 style. */
function groupPhone(digits: string): string {
  if (!digits) return ''
  const clean = digits.slice(0, 11)
  if (clean.length <= 4) return clean
  if (clean.length <= 7) return `${clean.slice(0, 4)} ${clean.slice(4)}`
  return `${clean.slice(0, 4)} ${clean.slice(4, 7)} ${clean.slice(7)}`.trim()
}

function toLocalDisplay(value: string | null | undefined): string {
  if (!value) return ''
  let d = normalizePhoneVN(value)
  if (d.startsWith('84')) d = '0' + d.slice(2)
  return d
}

function calculateCursorPosition(formatted: string, targetDigits: number): number {
  if (targetDigits <= 0) return 0
  let count = 0
  for (let i = 0; i < formatted.length; i++) {
    const ch = formatted[i]
    if (ch && /\d/.test(ch)) {
      count++
      if (count === targetDigits) return i + 1
    }
  }
  return formatted.length
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onValueChange,
      className,
      containerClassName,
      placeholder = '0912 345 678',
      error,
      showValidation = false,
      hideIcon = false,
      onFocus,
      onBlur,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const isComposingRef = React.useRef(false)
    const isFocusedRef = React.useRef(false)

    // Kết hợp forwardedRef và inputRef nội bộ
    React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

    const [text, setText] = React.useState(() => groupPhone(toLocalDisplay(value)))
    // Giá trị ô này vừa báo lên (onValueChange) — phân biệt với giá trị đặt từ bên ngoài
    const lastEmittedRef = React.useRef<string | null>(null)

    // Đồng bộ từ props value khi ô không focus, hoặc khi đổi từ bên ngoài lúc đang focus (chọn khách
    // quen trong gợi ý). Giá trị do chính ô này vừa báo lên → giữ nguyên chữ / con trỏ đang gõ.
    React.useEffect(() => {
      if (isFocusedRef.current && document.activeElement === inputRef.current && (value || null) === lastEmittedRef.current) {
        return
      }
      setText(groupPhone(toLocalDisplay(value)))
    }, [value])

    const processInput = React.useCallback(
      (raw: string, targetDigitsBeforeCursor: number) => {
        // Chuẩn hóa chuỗi nhập (đổi o/O thành 0, trích xuất số nếu dán kèm văn bản)
        const digitsOnly = normalizePhoneVN(raw).slice(0, 11)
        const newFormatted = groupPhone(digitsOnly)
        setText(newFormatted)

        // Đặt lại vị trí con trỏ chính xác sau số chữ số mong muốn
        const newPos = calculateCursorPosition(newFormatted, targetDigitsBeforeCursor)
        // Đồng bộ vị trí con trỏ ngay lập tức và qua microtask để tránh xung đột trình duyệt
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newPos, newPos)
        }
        requestAnimationFrame(() => {
          if (inputRef.current && document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPos, newPos)
          }
        })

        lastEmittedRef.current = digitsOnly || null
        if (!digitsOnly) {
          onValueChange(null)
          return
        }

        onValueChange(digitsOnly)
      },
      [onValueChange],
    )

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      if (isComposingRef.current) {
        setText(e.target.value)
        return
      }

      const raw = e.target.value
      const cursorPos = e.target.selectionStart ?? raw.length

      // Đếm số chữ số nằm trước vị trí con trỏ trong chuỗi mới vừa gõ
      let digitsBeforeCursor = 0
      for (let i = 0; i < cursorPos; i++) {
        if (/\d/.test(raw.charAt(i))) {
          digitsBeforeCursor++
        }
      }

      processInput(raw, digitsBeforeCursor)
    }

    function handleKeyDownEvent(e: React.KeyboardEvent<HTMLInputElement>) {
      // Xử lý thông minh khi nhấn Backspace trên khoảng trắng phân tách
      if (e.key === 'Backspace') {
        const el = inputRef.current
        if (el && el.selectionStart === el.selectionEnd && el.selectionStart !== null) {
          const pos = el.selectionStart
          // Nếu ký tự ngay trước con trỏ là dấu cách (ví dụ "0912 |345"):
          if (pos > 0 && text.charAt(pos - 1) === ' ') {
            e.preventDefault()
            // Xóa dấu cách và xóa luôn chữ số trước dấu cách
            const rawBefore = text.slice(0, pos - 2)
            const rawAfter = text.slice(pos)
            const combined = rawBefore + rawAfter
            let digitsBefore = 0
            for (let i = 0; i < pos - 2; i++) {
              if (/\d/.test(rawBefore.charAt(i))) digitsBefore++
            }
            processInput(combined, digitsBefore)
            return
          }
        }
      }
      onKeyDown?.(e)
    }

    function handleCompositionStart() {
      isComposingRef.current = true
    }

    function handleCompositionEnd(e: React.CompositionEvent<HTMLInputElement>) {
      isComposingRef.current = false
      const raw = e.currentTarget.value
      const cursorPos = e.currentTarget.selectionStart ?? raw.length
      let digitsBefore = 0
      for (let i = 0; i < cursorPos; i++) {
        if (/\d/.test(raw.charAt(i))) digitsBefore++
      }
      processInput(raw, digitsBefore)
    }

    function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
      isFocusedRef.current = true
      onFocus?.(e)
    }

    function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
      isFocusedRef.current = false
      // Khi rời ô, format lại theo text hiện tại
      const digits = text.replace(/\D/g, '')
      setText(groupPhone(digits))
      onBlur?.(e)
    }

    const currentDigits = text.replace(/\D/g, '')
    const isCompleteAndValid = isValidVnPhone(currentDigits)
    const validationError = React.useMemo(() => {
      if (error) return error
      if (!showValidation || !currentDigits) return null
      return getPhoneValidationError(currentDigits, false)
    }, [error, showValidation, currentDigits])

    return (
      <div className={cn('w-full', containerClassName)}>
        <div className="relative">
          {!hideIcon && <Phone className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted z-10" />}
          <input
            ref={inputRef}
            data-slot="input"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDownEvent}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={cn(
              'flex h-11 w-full rounded-md border border-rule bg-raise pr-9 py-2 text-base sm:text-sm text-ink tabular-nums',
              hideIcon ? 'pl-3' : 'pl-10',
              'placeholder:text-muted',
              'transition-shadow',
              'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              validationError
                ? 'border-danger focus-visible:ring-danger/20'
                : showValidation && isCompleteAndValid
                ? 'border-ok/70 focus-visible:ring-ok/20'
                : '',
              className,
            )}
            {...props}
          />
          {showValidation && isCompleteAndValid && (
            <CheckCircle2 className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-4 text-ok" />
          )}
        </div>
        {validationError && (
          <p className="text-xs text-danger mt-1.5 flex items-center gap-1">
            <AlertCircle className="size-3.5 shrink-0" />
            <span>{validationError}</span>
          </p>
        )}
      </div>
    )
  },
)
PhoneInput.displayName = 'PhoneInput'
